const QUALITY_STEPS = [0.9, 0.7, 0.5, 0.3, 0.1];

/**
 * Compress files based on their type
 * @param {File[]} files - Array of files to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File[]>} Compressed files
 */
async function compressFiles(files, options = {}) {
  const {
    maxTotalSizeMB = 4.5,
    maxFileSizeMB = 4.5,
    imageOptions = {},
  } = options;

  // Image compression utility
  const compressImage = async file => {
    const { maxWidthOrHeight = 1920, qualitySteps = QUALITY_STEPS } =
      imageOptions;

    const createImageBitmap = async file => {
      return await window.createImageBitmap(file);
    };

    const compressToMaxSize = async (imageBitmap, quality) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Scale down if image is larger than max dimensions
      let scale = 1;
      if (
        imageBitmap.width > maxWidthOrHeight ||
        imageBitmap.height > maxWidthOrHeight
      ) {
        scale = Math.min(
          maxWidthOrHeight / imageBitmap.width,
          maxWidthOrHeight / imageBitmap.height
        );
      }

      canvas.width = imageBitmap.width * scale;
      canvas.height = imageBitmap.height * scale;

      ctx.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height);

      return new Promise(resolve => {
        canvas.toBlob(
          blob => {
            resolve(blob);
          },
          'image/jpeg',
          quality
        );
      });
    };

    try {
      const imageBitmap = await createImageBitmap(file);

      for (let quality of qualitySteps) {
        const compressedBlob = await compressToMaxSize(imageBitmap, quality);

        // Check if compressed blob is under maxFileSizeMB
        if (compressedBlob.size <= maxFileSizeMB * 1000 * 1000) {
          return new File([compressedBlob], file.name, {
            type: 'image/jpeg',
          });
        }
      }

      // If all compression attempts fail, return the smallest possible compression
      const finalBlob = await compressToMaxSize(imageBitmap, 0.1);
      return new File([finalBlob], file.name, { type: 'image/jpeg' });
    } catch (error) {
      console.error(`Image compression failed for ${file.name}:`, error);
      return null;
    }
  };

  // Supported file types for compression
  const supportedImageTypes = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/bmp',
  ];

  // Compress files based on type
  const compressedFiles = await Promise.all(
    files.map(async file => {
      if (supportedImageTypes.includes(file.type)) {
        return await compressImage(file);
      }
      return file; // Return uncompressed for unsupported types
    })
  );

  // Remove any null results (failed compressions)
  const validFiles = compressedFiles.filter(file => file !== null);

  // Check total size
  const totalSize = validFiles.reduce((sum, file) => sum + file.size, 0);

  // If total size exceeds limit, further compress
  if (totalSize > maxTotalSizeMB * 1000 * 1000) {
    // Sort files by size, largest first
    validFiles.sort((a, b) => b.size - a.size);

    // Progressively remove or further compress largest files
    let attemptNumber = 0;
    while (
      validFiles.reduce((sum, file) => sum + file.size, 0) >
        maxTotalSizeMB * 1000 * 1000 &&
      validFiles.length > 0 &&
      attemptNumber < QUALITY_STEPS.length * validFiles.length
    ) {
      console.log('attemptNumber: ', attemptNumber);
      // Remove or further compress the largest file
      const largestFile = validFiles.shift();

      // Compress based on file type
      let furtherCompressedFile;
      if (supportedImageTypes.includes(largestFile.type)) {
        furtherCompressedFile = await compressImage(largestFile);
      } else {
        furtherCompressedFile = largestFile;
      }

      // Add back if compression was successful
      if (furtherCompressedFile) {
        validFiles.push(furtherCompressedFile);
      }
      attemptNumber++;
    }
  }

  const totalFileSize = validFiles.reduce((sum, file) => sum + file.size, 0);
  console.log('totalFileSize: ', totalFileSize);
  console.log('maxTotalSizeMB: ', maxTotalSizeMB * 1000 * 1000);

  return { validFiles, totalFileSize };
}

export default compressFiles;
